import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  GenericSignal,
  Pagination,
  Pixel,
  Signal,
  SignalThreadAccessEvent,
  SignalType,
} from "@types";
import { ApiResponseData, client, ExtendedAxiosRequestConfig } from "@api";
import { getUpdatedSignalsList } from "@utils";
import { RootState } from "../store";
import { AxiosError } from "axios";

export const getSignals = createAsyncThunk<{
  results: Signal[];
  pagination: Pagination & {
    notViewedSignalsCount: number;
    unreadSignalsCount: number;
  };
}>(
  "signals/getSignals",
  async (_, { getState, fulfillWithValue, rejectWithValue, signal }) => {
    try {
      const {
        auth: { user },
        signals: {
          filters: { dateRangeString, eventName },
          results: currentSignals,
          pagination: { page, nextPage },
        },
      } = getState() as RootState;

      const [fromTs, toTs] = dateRangeString.split("|");

      const params = new URLSearchParams();

      if (nextPage) {
        params.append("p", String(page + 1));
      }

      if (fromTs) {
        params.append("fromTs", fromTs);
      }

      if (toTs) {
        params.append("toTs", toTs);
      }

      if (Intl?.DateTimeFormat) {
        params.append("tz", Intl.DateTimeFormat().resolvedOptions().timeZone);
      }

      (eventName.length ? eventName : Object.values(SignalType)).forEach((name) => {
        params.append("eventName", name);
      });

      const response = await client.get<
        ApiResponseData<
          Pagination & {
            results: Signal[] | null;
            notViewedSignalsCount: number;
            unreadSignalsCount: number;
          }
        >
      >(`/services/orgs/${user?.orgId}/users/${user?.userId}/signals`, {
        signal,
        params,
        isEncrypted: true,
      } as ExtendedAxiosRequestConfig);

      const { results: newSignals, ...paginationResponse } = response.data.payload;

      return fulfillWithValue({
        results: getUpdatedSignalsList(currentSignals, newSignals),
        pagination: paginationResponse,
      });
    } catch (error) {
      return rejectWithValue(null);
    }
  },
  {
    condition: (_, { getState }) => {
      const {
        auth: { user },
      } = getState() as RootState;

      return !!user;
    },
  }
);

export const getThreadAccessEvents = createAsyncThunk<
  {
    results: SignalThreadAccessEvent[];
    pagination: Pagination;
  },
  {
    signal: GenericSignal<SignalType.EmailOpened>;
  }
>(
  "signals/getThreadAccessEvents",
  async ({ signal }, { getState, fulfillWithValue, rejectWithValue, signal: abortSignal }) => {
    try {
      const {
        auth: { user },
      } = getState() as RootState;

      const { accessEvents, thread } = signal.eventMetadata || {};
      const { nextPage, page } = accessEvents || {};
      const { platformThreadId = "" } = thread || {};

      const response = await client.get<
        ApiResponseData<
          Pagination & {
            results: SignalThreadAccessEvent[] | null;
          }
        >
      >(
        `/api/v1/pixel-tracking/orgs/${user?.orgId}/users/${user?.userId}/threads/${platformThreadId}/access-events`,
        {
          signal: abortSignal,
          ...(nextPage && page && { params: { p: page + 1 } }),
        }
      );

      const { results, ...paginationResponse } = response.data.payload;

      return fulfillWithValue({
        results: results || [],
        pagination: paginationResponse,
      });
    } catch (error) {
      return rejectWithValue(null);
    }
  },
  {
    condition: (_, { getState }) => {
      const {
        auth: { user },
        signals: { isLoading, selectedSignal },
      } = getState() as RootState;

      return !!user && !!selectedSignal && !isLoading.threadAccessEvents;
    },
  }
);

export const updateReadSignals = createAsyncThunk<string[], string[]>(
  "signals/updateReadSignals",
  async (eventIds, { getState, fulfillWithValue, rejectWithValue, signal }) => {
    try {
      const {
        auth: { user },
      } = getState() as RootState;

      await client.post<ApiResponseData<null>>(
        `/services/orgs/${user?.orgId}/users/${user?.userId}/signals/read`,
        eventIds,
        { signal }
      );

      return fulfillWithValue(eventIds);
    } catch (error) {
      return rejectWithValue(null);
    }
  },
  {
    condition: (_, { getState }) => {
      const {
        auth: { user },
      } = getState() as RootState;

      return !!user;
    },
  }
);

export const updateViewedSignals = createAsyncThunk(
  "signals/updateViewedSignals",
  async (_, { getState, fulfillWithValue, rejectWithValue, signal }) => {
    try {
      const {
        auth: { user },
      } = getState() as RootState;

      await client.post<ApiResponseData<null>>(
        `/services/orgs/${user?.orgId}/users/${user?.userId}/signals/view`,
        undefined,
        {
          signal,
          params: {
            all: true,
          },
        }
      );

      return fulfillWithValue(null);
    } catch (error) {
      return rejectWithValue(null);
    }
  },
  {
    condition: (_, { getState }) => {
      const {
        auth: { user },
      } = getState() as RootState;

      return !!user;
    },
  }
);

export const getThreadTrackStatus = createAsyncThunk<
  { onTrack: boolean; platformThreadId: string },
  { threadId: string }
>(
  "signals/getThreadTrackStatus",
  async ({ threadId }, { getState, fulfillWithValue, rejectWithValue, signal }) => {
    try {
      const {
        auth: { user },
      } = getState() as RootState;

      const response = await client.get<
        ApiResponseData<{ platformThreadId: string; onTrack: boolean }>
      >(
        `/api/v1/pixel-tracking/orgs/${user?.orgId}/users/${user?.userId}/threads/${threadId}/track-status`,
        { signal }
      );

      return fulfillWithValue(response.data.payload);
    } catch (error) {
      return rejectWithValue(null);
    }
  },
  {
    condition: (_, { getState }) => {
      const {
        auth: { user },
      } = getState() as RootState;

      return !!user;
    },
  }
);

export const updateThreadTrackStatus = createAsyncThunk<
  boolean,
  {
    threadId: string;
    onTrack: boolean;
  }
>(
  "gmailTrackify/updateThreadTrackStatus",
  async ({ threadId, onTrack }, { getState, fulfillWithValue, rejectWithValue, signal }) => {
    try {
      const {
        auth: { user },
      } = getState() as RootState;

      const response = await client.patch<ApiResponseData<{ success: boolean; message: string }>>(
        `/api/v1/pixel-tracking/orgs/${user?.orgId}/users/${user?.userId}/threads/${threadId}`,
        { onTrack },
        { signal }
      );

      return fulfillWithValue(response.data.payload.success ? onTrack : !onTrack);
    } catch (error) {
      return rejectWithValue(null);
    }
  },
  {
    condition: (_, { getState }) => {
      const {
        auth: { user },
      } = getState() as RootState;

      return !!user;
    },
  }
);

export const createPixel = createAsyncThunk<
  Pixel,
  { dataMessageId: string; platformThreadId?: string }
>(
  "signals/createPixel",
  async (
    { dataMessageId, platformThreadId },
    { getState, fulfillWithValue, rejectWithValue, signal }
  ) => {
    try {
      const {
        auth: { user },
      } = getState() as RootState;

      const response = await client.post<
        ApiResponseData<{ success: boolean; url: string; platformThreadId: string }>
      >(
        `/api/v1/pixel-tracking/orgs/${user?.orgId}/users/${user?.userId}/p`,
        {
          dataMessageId,
          platformThreadId: platformThreadId || "",
          platform: "outlook",
        },
        { signal }
      );

      return fulfillWithValue({
        url: response.data.payload.url,
        dataMessageId,
        platformThreadId: platformThreadId || "",
      });
    } catch (error) {
      const { code, message, response } = error as AxiosError<ApiResponseData>;

      return rejectWithValue({
        dataMessageId,
        platformThreadId: platformThreadId || "",
        code: response?.data?.code || code,
        message: response?.data?.error || message,
      });
    }
  },
  {
    condition: (_, { getState }) => {
      const {
        auth: { user },
      } = getState() as RootState;

      return !!user;
    },
  }
);

export const triggerEmailSentEvent = createAsyncThunk<
  string,
  { dataMessageId: string; platformThreadId?: string }
>(
  "signals/triggerEmailSentEvent",
  async (
    { dataMessageId, platformThreadId },
    { getState, fulfillWithValue, rejectWithValue, signal }
  ) => {
    try {
      const {
        auth: { user },
      } = getState() as RootState;

      await client.post<ApiResponseData<{ success: boolean; message: string }>>(
        `/api/v1/pixel-tracking/orgs/${user?.orgId}/users/${user?.userId}/events`,
        {
          dataMessageId,
          event_type: "emailSent",
          platformThreadId: platformThreadId || "",
        },
        { signal }
      );

      return fulfillWithValue(dataMessageId);
    } catch (error) {
      return rejectWithValue(null);
    }
  },
  {
    condition: (_, { getState }) => {
      const {
        auth: { user },
      } = getState() as RootState;

      return !!user;
    },
  }
);
